/* eslint-disable no-restricted-globals */
import React, { Fragment, useState, useContext, useEffect } from 'react';
import { differenceBy } from 'lodash-es';
import { makeStyles } from '@material-ui/core/styles';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { ActionIcon } from '@mummssoftware/common-ui';
import {
  GlobalContext,
  OPEN_WIDGET,
  CLOSE_WIDGET,
} from '@mummssoftware/global-context/GlobalContext';
import { Forms, CompletedForms } from '@mummssoftware/utils/constants';
import { isPhoneDevice } from '@mummssoftware/utils/web';
import AddToPhotosIcon from '@material-ui/icons/AddToPhotos';
import PlaylistAddIcon from '@material-ui/icons/PlaylistAdd';
import { styleIcon } from '../../components';
import defaults from '../../translations/en.json';

const translations: NonNullable<mumms.JSONObject> = defaults;

type WidgetMenuProps = {
  config: mumms.WidgetConfig[];
  isIDGMeeting: boolean;
};
type WidgetPlusMenuProps = {
  config: mumms.WidgetConfig[];
  onAddPatientDashboardWidget?: (selectedItem: mumms.WidgetConfig) => void;
};
type BaseWidgetMenuProps = WidgetMenuProps & {
  Icon?: React.ComponentType<any>;
  alwaysShow?: boolean;
  showWhenClosed?: boolean;
  /** When set (patient dashboard plus menu), adds to layout instead of opening a floating widget. */
  onMenuItemSelect?: (selectedItem: mumms.WidgetConfig) => void;
};

const ITEM_HEIGHT = 45;

const useStyles = makeStyles(() => ({
  menuPaper: { maxHeight: ITEM_HEIGHT * 4, minWidth: 160 },
  menuItem: {
    '&.MuiMenuItem-root': {
      fontSize: '1rem',
      lineHeight: 1,
    },
  },
}));

const BaseWidgetMenu = ({
  config,
  isIDGMeeting,
  Icon = AddToPhotosIcon,
  alwaysShow,
  showWhenClosed,
  onMenuItemSelect,
}: BaseWidgetMenuProps) => {
  const classes = useStyles();

  const { widgetsOpen } = useContext(GlobalContext);
  const globalContext = useContext(GlobalContext);

  const isPhone = isPhoneDevice();
  const showMenu = showWhenClosed || isIDGMeeting || !!widgetsOpen.length;
  // phone => we only want the plus dropdown to show up to 2 widget opened
  const isPhoneMaxWidgetOpen = showMenu && widgetsOpen.length < 2;
  // computer => we only want the plus dropdown to show up to 4 widget opened
  const isScreenMaxWidgetOpen = showMenu && widgetsOpen.length < 4;
  const isMaxWidgetOpen = isPhone
    ? isPhoneMaxWidgetOpen
    : isScreenMaxWidgetOpen;

  // widgets in the dropdown should not show the widgets currently open
  const [widgetDropdown, setWidgetDropdown] = useState<mumms.WidgetConfig[]>(
    [],
  );

  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    if (showMenu) {
      const widgetConfig = config.filter(
        (widget) => widget.expandable === true,
      );

      const listDropDown = differenceBy(
        widgetConfig,
        widgetsOpen,
        (x: mumms.WidgetConfig) => x.componentName,
      );
      setWidgetDropdown(listDropDown);
    }
  }, [showMenu, config, widgetsOpen]);

  const onOpen = (e: any) => {
    setAnchorEl(e.currentTarget);
  };

  const onClose = () => {
    setAnchorEl(null);
  };

  const onMenuItemClick = (selectedItem: mumms.WidgetConfig) => () => {
    if (onMenuItemSelect) {
      onMenuItemSelect(selectedItem);
    } else {
      globalContext.dispatch({
        type: OPEN_WIDGET,
        payload: selectedItem,
      });
    }
    setAnchorEl(null);
  };

  return alwaysShow || isMaxWidgetOpen ? (
    <Fragment>
      <ActionIcon
        title={translations.sideMenuWidget}
        style={styleIcon}
        Icon={Icon}
        onClick={onOpen}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onClose}
        PaperProps={{
          classes: {
            elevation8: classes.menuPaper,
          },
        }}
      >
        {(widgetDropdown as mumms.WidgetConfig[]).map(
          (widget: mumms.WidgetConfig) => (
            <MenuItem
              key={widget.widgetName}
              onClick={onMenuItemClick(widget)}
              className={classes.menuItem}
            >
              {widget.widgetName}
            </MenuItem>
          ),
        )}
      </Menu>
    </Fragment>
  ) : null;
};

export const WidgetMenu = ({ config, isIDGMeeting }: WidgetMenuProps) => (
  <BaseWidgetMenu config={config} isIDGMeeting={isIDGMeeting} />
);

export const WidgetPlusMenu = ({
  config,
  onAddPatientDashboardWidget,
}: WidgetPlusMenuProps) =>
  config.length ? (
    <BaseWidgetMenu
      config={config}
      isIDGMeeting={false}
      Icon={PlaylistAddIcon}
      alwaysShow
      showWhenClosed
      onMenuItemSelect={onAddPatientDashboardWidget}
    />
  ) : null;

export default WidgetMenu;
